import {ApiResource} from 'common/util/model-resource/api-resource';
import {Model, ModelStatus} from 'common/util/model-resource/model-resource';

import {
  deleteLookupTableDefinitions,
  getById,
  LookupTableDefinition,
  LookupTableDefinitionDetails,
  lookupTablesClient,
  updateLookupTableDefinition,
} from '../queries/app-api/lookup-tables-client';

export type {LookupTableDefinition};

// TODO - revisit null casting
export class LookupTablesResource extends ApiResource<LookupTableDefinition> {
  apiClient = lookupTablesClient;

  getDetails<PropertyDefinitionType>(id: string): Model<LookupTableDefinitionDetails<PropertyDefinitionType>> {
    const existing = this.getOne(id) as Model<LookupTableDefinitionDetails<PropertyDefinitionType>>;
    if (existing.value?.mappedProperties || existing.status !== ModelStatus.Success) {
      // either we already have what we want, we're still waiting, or we can't get it
      return existing;
    }

    if (this.fetchesInProgress.has(id)) {
      return this.loadingModel();
    }

    // @ts-ignore
    this.fetchesInProgress.set(id, {});
    getById(id)
      .then((result: Array<LookupTableDefinitionDetails<PropertyDefinitionType>>) => {
        this.fetchesInProgress.delete(id);

        if (result[0].id === id) {
          this.update({[id]: result[0]}, {partialUpdate: false});
        } else {
          throw new Error(`Lookup table with id ${id} not found`);
        }
      })
      .catch(() => {
        return this.notFoundModel();
      });

    return this.loadingModel();
  }

  async patch(id: string, propsToUpdate: Partial<LookupTableDefinition>) {
    // optimistic update
    const copy = this.getOne(id).value;
    this.update({[id]: propsToUpdate});
    try {
      await updateLookupTableDefinition(id, propsToUpdate);
    } catch (error) {
      this.update({[id]: copy}, {partialUpdate: false});
      throw error;
    }
  }

  async delete(id: string) {
    // optimistic update
    super.delete(id);
    try {
      await deleteLookupTableDefinitions([id]);
    } catch (error) {
      const copy = this.getOne(id).value;
      super.update({[id]: copy}, {partialUpdate: false});
      throw error;
    }
  }

  async bulkDelete(ids: string[]) {
    // optimistic update
    const copy = this.getAll();
    ids.forEach((id) => {
      super.delete(id);
    });

    try {
      await deleteLookupTableDefinitions(ids);
    } catch (error) {
      // if status is not success, revert the update
      ids.forEach((id) => {
        copy?.get(id)?.value && this.update({[id]: copy.get(id)!.value!}, {partialUpdate: false});
      });

      throw error;
    }
  }
}
